/**
 * Classificação das linhas do extrato NO USO (depende de `hoje`, por isso
 * não vai na foto): situação, camada do aberto, data de entrega e sinais de
 * qualidade.
 */

export const SITUACAO = Object.freeze({
  TERMINADO: "terminado",
  ABERTO: "aberto",
  SERVICO: "servico",
  CANCELADO: "cancelado",
});

/** Camadas do aberto, na ordem de precedência (plano 4.1). */
export const CAMADA = Object.freeze({
  EM_PRODUCAO: "em_producao",
  OBRA_FUTURA: "obra_futura",
  APROVADO: "aprovado",
  AGUARDANDO_APROVACAO: "aguardando_aprovacao",
  // Fora da planilha: venda assinada registrada na página, aguardando o PCP
  // digitá-la (vem do backend, `useVendasAssinadas`)...
  VENDA_ASSINADA: "venda_assinada",
  // ...e os ambientes fictícios de "Simular venda" (nunca gravados).
  VENDA_SIMULADA: "venda_simulada",
});

/** Camadas que consomem capacidade. Obra futura sem início é só "potencial". */
export const CAMADAS_DA_FILA = Object.freeze([
  CAMADA.EM_PRODUCAO,
  CAMADA.APROVADO,
  CAMADA.AGUARDANDO_APROVACAO,
]);

export const SINAL = Object.freeze({
  ERRO_FORMULA_ENTREGA: "erro_formula_entrega",
  SEM_DATA_ENTREGA: "sem_data_entrega",
  TERMINO_FUTURO: "termino_futuro",
  TERMINO_ANTES_INICIO: "termino_antes_inicio",
});

/**
 * Data de entrega de referência — a mesma régua de Entregas e Relatórios
 * (`getEntregaDeadline`): reprogramada se válida, senão a do contrato.
 * Reprogramada com erro de fórmula NÃO cai para o contrato: a data do
 * contrato é a que o PCP já mudou, e usá-la esconderia o problema.
 */
export function entregaDaLinha(linha) {
  if (linha.data_reprogramada) return { entrega: linha.data_reprogramada, sinal: null };
  if ((linha.erros || []).includes("data_reprogramada")) {
    return { entrega: null, sinal: SINAL.ERRO_FORMULA_ENTREGA };
  }
  if (linha.entrega_contrato) return { entrega: linha.entrega_contrato, sinal: null };
  return { entrega: null, sinal: SINAL.SEM_DATA_ENTREGA };
}

export function camadaDoAberto(linha) {
  if (linha.marcenaria_inicio) return CAMADA.EM_PRODUCAO;
  if (linha.obra_futura) return CAMADA.OBRA_FUTURA;
  if (linha.aprovacao) return CAMADA.APROVADO;
  return CAMADA.AGUARDANDO_APROVACAO;
}

function situacaoDa(linha, hoje) {
  if (linha.cancelado) return SITUACAO.CANCELADO;
  if (linha.servico) return SITUACAO.SERVICO;
  // Término no futuro é lançamento antecipado/errado: conta como aberto.
  if (linha.termino && linha.termino <= hoje) return SITUACAO.TERMINADO;
  return SITUACAO.ABERTO;
}

function classificarLinha(linha, hoje) {
  const situacao = situacaoDa(linha, hoje);
  const sinais = [];
  const base = { ...linha, situacao, camada: null, entrega: null, sinais };
  if (situacao === SITUACAO.TERMINADO) {
    if (linha.marcenaria_inicio && linha.termino < linha.marcenaria_inicio) {
      sinais.push(SINAL.TERMINO_ANTES_INICIO);
    }
    return base;
  }
  if (situacao !== SITUACAO.ABERTO) return base;
  if (linha.termino) sinais.push(SINAL.TERMINO_FUTURO);
  const { entrega, sinal } = entregaDaLinha(linha);
  if (sinal) sinais.push(sinal);
  return { ...base, camada: camadaDoAberto(linha), entrega };
}

/**
 * `{linhas, duplicadas}`. Chave repetida no arquivo: vale a ÚLTIMA linha
 * (é a mais recente que o PCP digitou); as anteriores saem e o grupo vai
 * para `duplicadas` ({chave, linhas}) como aviso. Linha sem chave (sem
 * pedido ou sem ambiente) não é deduplicada.
 */
export function classificarLinhas(linhas, hoje) {
  const ultimaPorChave = new Map();
  const grupos = new Map();
  (linhas || []).forEach((linha, indice) => {
    if (!linha.chave) return;
    ultimaPorChave.set(linha.chave, indice);
    grupos.set(linha.chave, [...(grupos.get(linha.chave) || []), linha]);
  });
  const mantidas = (linhas || []).filter(
    (linha, indice) => !linha.chave || ultimaPorChave.get(linha.chave) === indice,
  );
  const duplicadas = [...grupos.entries()]
    .filter(([, grupo]) => grupo.length > 1)
    .map(([chave, grupo]) => ({ chave, linhas: grupo }));
  return { linhas: mantidas.map((linha) => classificarLinha(linha, hoje)), duplicadas };
}

/**
 * Términos para o ritmo e a precisão: os do backend (`TerminoLegado`, que
 * sobrevivem à virada de ano da planilha) unidos aos terminados do extrato
 * atual, por `identidade`. O extrato atual VENCE: se a identidade está na
 * planilha de agora, vale o que a planilha diz — inclusive "reaberto" (sem
 * término), serviço ou cancelado, que tiram o término da conta.
 * Retorna `[{identidade, chave, termino, cliente}]` com termino ≤ hoje.
 */
export function unirTerminos(terminosBackend, linhasClassificadas, hoje) {
  const decididasPeloExtrato = new Set();
  const porIdentidade = new Map();
  (linhasClassificadas || []).forEach((linha) => {
    if (linha.identidade) decididasPeloExtrato.add(linha.identidade);
    if (linha.situacao !== SITUACAO.TERMINADO) return;
    // Sem chave não há como casar com o backend, mas o término aconteceu e
    // conta no ritmo de agora.
    const identidade = linha.identidade || `sem-chave:${linha.linha_planilha}`;
    porIdentidade.set(identidade, {
      identidade,
      chave: linha.chave,
      cliente: linha.cliente,
      termino: linha.termino,
    });
  });
  (terminosBackend || []).forEach((termino) => {
    if (!termino?.identidade || !termino.termino) return;
    if (decididasPeloExtrato.has(termino.identidade) || termino.termino > hoje) return;
    porIdentidade.set(termino.identidade, {
      identidade: termino.identidade,
      chave: termino.chave,
      cliente: termino.cliente,
      termino: termino.termino,
    });
  });
  return [...porIdentidade.values()];
}
